import { api } from './index';
import { MeterReadingType } from './meterReadingsApi';

export type UtilityMeterBillingMode = 'QUOTA' | 'NON_QUOTA';
export type UtilityMeterQuotaTier = 'TIER1' | 'TIER2' | 'TIER3' | 'TIER4';
export type UtilityMeterStatus = 'ACTIVE' | 'INACTIVE';

export interface UtilityMeter {
  id: string;
  buildingId: string;
  apartmentId: string;
  meterCode: string;
  qrCode?: string | null;
  type: MeterReadingType;
  billingMode: UtilityMeterBillingMode;
  feeMultiplier?: number | null;
  quotaTierStart: UtilityMeterQuotaTier;
  quotaTierLevel: number;
  registrationDate?: string | null;
  initialReading: number;
  status: UtilityMeterStatus;
  notes?: string | null;
  apartmentCode?: string;
  apartmentName?: string;
  oldReading?: number | null;
  newReading?: number;
  usage?: number;
  hasExistingReading?: boolean;
  existingReadingId?: string;
  imagePath?: string;
}

export interface PaginatedMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginatedMeta;
}

export interface CreateUtilityMeterData {
  buildingId: string;
  apartmentId: string;
  meterCode: string;
  qrCode?: string;
  type: MeterReadingType;
  billingMode?: UtilityMeterBillingMode;
  feeMultiplier?: number;
  quotaTierStart?: UtilityMeterQuotaTier;
  quotaTierLevel?: number;
  registrationDate?: string;
  initialReading?: number;
  status?: UtilityMeterStatus;
  notes?: string;
}

export interface UpdateUtilityMeterData {
  meterCode?: string;
  qrCode?: string;
  billingMode?: UtilityMeterBillingMode;
  feeMultiplier?: number | null;
  quotaTierStart?: UtilityMeterQuotaTier;
  quotaTierLevel?: number;
  registrationDate?: string;
  initialReading?: number;
  status?: UtilityMeterStatus;
  notes?: string;
}

export interface ListUtilityMetersParams {
  buildingId: string;
  type: MeterReadingType;
  period?: string;
  search?: string;
  page?: number;
  limit?: number;
  recordedOnly?: boolean;
}

export type UtilityMeterRegistryAction = 'DELETE' | 'BULK_DELETE' | 'IMPORT';

export interface UtilityMeterActionLog {
  id: string;
  buildingId: string;
  utilityMeterId?: string | null;
  apartmentId?: string | null;
  type: MeterReadingType;
  action: UtilityMeterRegistryAction;
  meterCode?: string | null;
  apartmentCode?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  creator?: { id: string; name?: string | null; email?: string | null } | null;
  fileName?: string | null;
  fileUrl?: string | null;
}

function mapMeterListResponse(data: UtilityMeter[]): UtilityMeter[] {
  return data;
}

export const utilityMetersApi = {
  async list(params: ListUtilityMetersParams): Promise<PaginatedResponse<UtilityMeter>> {
    const query = new URLSearchParams();
    query.append('buildingId', params.buildingId);
    query.append('type', params.type);
    if (params.period) query.append('period', params.period);
    if (params.search) query.append('search', params.search);
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    if (params.recordedOnly) query.append('recordedOnly', 'true');
    const response = await api.get(`/utility-meters?${query.toString()}`);
    return {
      data: mapMeterListResponse(response.data.data ?? response.data),
      meta: response.data.meta ?? {
        total: (response.data.data ?? response.data).length,
        page: 1,
        limit: (response.data.data ?? response.data).length,
        totalPages: 1,
      },
    };
  },

  async listAll(params: Omit<ListUtilityMetersParams, 'page' | 'limit'>): Promise<UtilityMeter[]> {
    const all: UtilityMeter[] = [];
    let page = 1;
    let totalPages = 1;
    while (page <= totalPages) {
      const result = await this.list({ ...params, page, limit: 100 });
      all.push(...result.data);
      totalPages = result.meta.totalPages;
      page += 1;
    }
    return all;
  },

  async create(data: CreateUtilityMeterData): Promise<UtilityMeter> {
    const response = await api.post('/utility-meters', data);
    return response.data;
  },

  async update(id: string, data: UpdateUtilityMeterData): Promise<UtilityMeter> {
    const response = await api.put(`/utility-meters/${id}`, data);
    return response.data;
  },

  async remove(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/utility-meters/${id}`);
    return response.data;
  },

  async bulkDelete(params: {
    buildingId: string;
    type: MeterReadingType;
    meterIds: string[];
  }): Promise<{ deletedCount: number; skippedCount: number; skipped: unknown[] }> {
    const response = await api.post('/utility-meters/bulk-delete', params);
    return response.data;
  },

  async getActionLogs(params: {
    buildingId: string;
    type: MeterReadingType;
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<PaginatedResponse<UtilityMeterActionLog>> {
    const query = new URLSearchParams();
    query.append('buildingId', params.buildingId);
    query.append('type', params.type);
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    if (params.search) query.append('search', params.search);
    const response = await api.get(`/utility-meters/action-logs?${query.toString()}`);
    return response.data;
  },

  async downloadExport(
    buildingId: string,
    type: MeterReadingType,
    endpoint: 'export' | 'export-sample',
  ): Promise<void> {
    const response = await api.get(`/utility-meters/${endpoint}?buildingId=${buildingId}&type=${type}`, {
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    const filename =
      filenameMatch?.[1] ||
      (type === MeterReadingType.WATER ? 'dong_ho_nuoc.xlsx' : 'dong_ho_dien.xlsx');
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  async exportMeters(buildingId: string, type: MeterReadingType): Promise<void> {
    return this.downloadExport(buildingId, type, 'export');
  },

  async exportSampleMeters(buildingId: string, type: MeterReadingType): Promise<void> {
    return this.downloadExport(buildingId, type, 'export-sample');
  },

  async importMeters(formData: FormData): Promise<{
    created: number;
    updated: number;
    skipped: number;
    errors: string[];
  }> {
    const response = await api.post('/utility-meters/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300_000,
    });
    return response.data;
  },
};
